import { ModalView } from './ModalView';
import type { Config, Options } from '../core/types';
import { ContentContainer, ProductType } from '../core/types';
import type { ITrackingService } from '../tracking/ITrackingService';
import type { IIframeProvider } from './IframeProvider';
import type { CalculatorData, IframeState } from './types';

// Handles interactions between ModalView, iframe management, and tracking.
export class ModalCoordinator {
  private static instance: ModalCoordinator | null = null;
  private view: ModalView;
  private iframeStates = new Map<string, IframeState>();
  private currentContainer: ContentContainer | null = null;

  private constructor(
    private config: Config,
    private tracker: ITrackingService
  ) {
    this.view = new ModalView(config);
    this.view.setOnCloseRequested(() => this.close());
  }

  static initialize(config: Config, tracker: ITrackingService): void {
    if (ModalCoordinator.instance) {
      throw new Error('ModalCoordinator already initialized');
    }
    ModalCoordinator.instance = new ModalCoordinator(config, tracker);
  }

  static getInstance(): ModalCoordinator {
    if (!ModalCoordinator.instance) {
      throw new Error('ModalCoordinator not initialized. Call initialize() first.');
    }
    return ModalCoordinator.instance;
  }

  open(
    dealerKey: string,
    options: Options,
    container: ContentContainer,
    iframeProvider: IIframeProvider
  ): void {
    if (this.view.isOpen()) {
      this.close();
    }

    let state = this.iframeStates.get(container.id);

    if (!state) {
      // First open - create new iframe
      const iframe = iframeProvider.createIframe(dealerKey, this.config, options, container);
      state = {
        iframe,
        originalProductType: container.productType,
        currentProductType: container.productType
      };
      this.iframeStates.set(container.id, state);
    } else if (this.shouldRevert(state)) {
      this.swapToOriginalProduct(dealerKey, options, container, iframeProvider);
      state = this.iframeStates.get(container.id)!;
    }
    // else: reuse existing iframe (preserves form state, no refetch)

    const headerText = this.getHeaderText(state.currentProductType);
    this.view.show(state.iframe, headerText);
    
    this.currentContainer = container;
    this.tracker.trackOpen(container);
  }

  swapToScreening(
    dealerKey: string,
    options: Options,
    container: ContentContainer,
    iframeProvider: IIframeProvider,
    calculatorData?: CalculatorData
  ): void {
    const screeningContainer = { ...container, productType: ProductType.SCREENING };
    const iframe = iframeProvider.createIframe(
      dealerKey,
      this.config,
      options,
      screeningContainer,
      true,
      calculatorData
    );
    
    const state = this.iframeStates.get(container.id);
    if (state) {
      state.iframe = iframe;
      state.currentProductType = ProductType.SCREENING;
    }

    const headerText = this.getHeaderText(ProductType.SCREENING);
    this.view.show(iframe, headerText);
  }

  close(): void {
    if (!this.view.isOpen() || !this.currentContainer) return;
    
    this.view.hide();
    this.tracker.trackClose(this.currentContainer);
    this.currentContainer = null;
  }

  private swapToOriginalProduct(
    dealerKey: string,
    options: Options,
    container: ContentContainer,
    iframeProvider: IIframeProvider
  ): void {
    const state = this.iframeStates.get(container.id);
    if (!state) return;

    const revertedContainer = { ...container, productType: state.originalProductType };
    const iframe = iframeProvider.createIframe(dealerKey, this.config, options, revertedContainer);
    
    state.iframe = iframe;
    state.currentProductType = state.originalProductType;
  }

  private shouldRevert(state: IframeState): boolean {
    return state.originalProductType === ProductType.CALCULATOR
      && state.currentProductType === ProductType.SCREENING;
  }

  private getHeaderText(productType: ProductType): string {
    return productType === ProductType.CALCULATOR
      ? this.config.copy.calculator.headerText
      : this.config.copy.form.headerText;
  }
}
